import * as firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";
// import { string } from "yup";
// import { incrementActiveRequestCount } from "./stats";

const store = firebase.firestore();

// compatible blood groups
const compatibleBg: any = {
  "O-": ["O-", "O+", "A-", "A+", "B-", "B+", "AB-", "AB+"],
  "O+": ["O-", "O+", "A+", "B+", "AB+"],
  "A-": ["O-", "O+", "A-", "A+", "AB-", "AB+"],
  "A+": ["O-", "O+", "A-", "A+", "AB+"],
  "B-": ["O-", "O+", "B-", "B+", "AB+", "AB+"],
  "B+": ["O-", "O+", "B-", "B+", "AB+"],
  "AB-": ["O-", "O+", "A-", "A+", "B-", "B+", "AB-", "AB+"],
  "AB+": ["O-", "O+", "A-", "A+", "B-", "B+", "AB-", "AB+"],
};

/**
 * Fetches and returns a single request with reqID from database.
 * @param reqID a 7-digit id for request
 */
export async function getRequest(reqID: string): Promise<object> {
  return new Promise((resolve, reject) => {
    store
      .collection("requests")
      .doc(reqID)
      .get()
      .then((snapshot) => {
        if (snapshot.data()) {
          resolve(snapshot.data());
        } else reject(null);
      });
  });
}

/**
 * Permanently deletes a request from database. Requires contact no verification
 * for a one-time requester.
 * @param reqID a 7-digit id for request
 * @param contactNo for ONE-TIME REQUESTER: associated contact no. with the request (for verification)
 */
export async function deleteRequest(
  reqID: string,
  contactNo?: string
): Promise<any> {
  return store
    .collection("requests")
    .doc(reqID)
    .get()
    .then(async (snapshot) => {
      const data: any = snapshot.data();

      // If request was made by requester
      if (data.requester) {
        // deleting request
        await store.collection("requests").doc(reqID).delete();
        // Removing request from requester list too
        return store
          .collection("requester")
          .doc(data.requester)
          .update({
            requests: firebase.firestore.FieldValue.arrayRemove(reqID),
          });
      } else if (
        contactNo &&
        data.contact.substr(data.contact.length - 10) ===
          contactNo.substr(contactNo.length - 10)
      ) {
        // else if request was made by one-time requester, we verify contactNo
        // deleting request
        return store.collection("requests").doc(reqID).delete();
      }
    })
    .catch((err) => {
      return false;
    });
}

/**
 * Changes request status from database.
 * @param reqID a 7-digit id for request
 * @param status set to true to activate request, and false for deactivate
 */
export async function changeRequestStatus(
  reqID: string,
  status: boolean
): Promise<any> {
  return store.collection("requests").doc(reqID).update({ isActive: status });
}

/**
 * Stores a user's blood request by taking submitted details.
 * @param requestData Object containing name, city, email, blood group, hospital, urgency, contact, transportation, and date.
 * @param requester ID of the requester making the request (default: empty)
 */
export async function storeRequest(requestData: any, requester: any) {
  const reqID = Math.floor(1000000 + Math.random() * 9000000).toString();
  store
    .collection("requests")
    .doc(reqID)
    .set({
      ...requestData,
      date: new Date(),
      contactsMessaged: [],
      replies: 0,
      isActive: true,
      requester,
      requestID: reqID,
    })
    .then(function () {
      // In case of requester making the request, we update the requester's requests
      if (requester) {
        store
          .collection("requester")
          .doc(requester)
          .update({
            requests: firebase.firestore.FieldValue.arrayUnion(reqID),
            requestsCounter: firebase.firestore.FieldValue.increment(1),
          });
      }
    })
    .catch(function (error) {
      // Add HTML for error while req generation
    });
  return reqID;
}

/**
 * Returns an array of all requests stored so far in database
 * @param None
 */
export async function getRequests() {
  const requests: any = [];
  await store
    .collection("requests")
    .get()
    .then((snapshot) => {
      snapshot.forEach((request) => {
        requests.push(request.data());
      });
    })
    .catch((err) => console.log("Error getting data", err));
  // return Promise.resolve(requests)
  return requests;
}

/**
 * Fetches and returns all active blood requests that match the
 * passed bloodGroup and city from the database.
 * @param bloodGroup blood group of the requests
 * @param city city of the requests
 */
export async function getActiveRequests(
  bloodGroup: string,
  city: string,
  covid: boolean
) {
  return new Promise((resolve, reject) => {
    const data: any[] = [];
    if (covid) {
      store
        .collection("requests")
        .where("city", "==", city)
        .where("bloodGroup", "in", compatibleBg[bloodGroup])
        .where("isActive", "==", true)
        .get()
        .then((snapshot) => {
          snapshot.forEach((request) => {
            data.push(request.data());
          });
          resolve(data);
        })
        .catch((err) => reject(err));
    } else {
      store
        .collection("requests")
        .where("city", "==", city)
        .where("bloodGroup", "in", compatibleBg[bloodGroup])
        .where("isActive", "==", true)
        .where("type", "==", "Blood")
        .get()
        .then((snapshot) => {
          snapshot.forEach((request) => {
            data.push(request.data());
          });
          resolve(data);
        })
        .catch((err) => reject(err));
    }
  });
}

/**
 * Fetches and returns all active blood requests made by
 * the given user (uid).
 * @param uid uid of the user
 */
export async function getRequestsOfUser(uid: string): Promise<Array<any>> {
  return new Promise((resolve, reject) => {
    const requests: any[] = [];
    store
      .collection("requester")
      .doc(uid)
      .get()
      .then((doc) => {
        if (doc.exists && doc.data()!.requests) {
          Promise.all(
            doc.data()!.requests.map(
              (reqID: any) =>
                new Promise((res) => {
                  if (reqID) {
                    getRequest(reqID).then((request) => {
                      requests.push(request);
                      res();
                    });
                  } else {
                    res();
                  }
                })
            )
          ).then(() => resolve(requests));
        } else {
          reject("Doc does not exist");
        }
      })
      .catch((err) => reject(err));
  });
}

/**
 * Deactivates a request from database by first verfiying with
 * contact number.
 * @param reqID a 7-digit id for request
 * @param contactNo the associated contact number with the request (for verification)
 */
export async function deactivateRequestWithVerify(
  reqID: string,
  contactNo: string
): Promise<boolean> {
  return store
    .collection("requests")
    .doc(reqID)
    .get()
    .then((snapshot) => {
      const data: any = snapshot.data();
      if (
        data.contact.substr(data.contact.length - 10) ===
        contactNo.substr(contactNo.length - 10)
      ) {
        store.collection("requests").doc(reqID).update({ isActive: false });
        return true;
      }
      return false;
    })
    .catch((err) => {
      return false;
    });
}

export async function checkPhoneNumberRequests(phoneNumber: string) {
  return store
    .collection("requests")
    .where("contact", "==", phoneNumber)
    .where("isActive", "==", true)
    .get()
    .then((querySnapshot) => {
      if (!querySnapshot.empty) {
        return true;
      } else {
        return false;
      }
    })
    .catch((error) => {
      return true;
    });
}

/**
 * Fetches and returns emails of all compatible donors that match the
 * passed bloodGroup and city from the database.
 * @param bloodGroup blood group of the requests
 * @param city city of the requests
 */
export async function getCompatibleDonors(
  bloodGroup: string,
  city: string,
  type: string
): Promise<Array<string>> {
  return new Promise((resolve, reject) => {
    const data: any[] = [];
    if (type === "Plasma") {
      store
        .collection("donor")
        .where("city", "==", city)
        .where("bloodGroup", "in", compatibleBg[bloodGroup])
        .where("covidRecovered", "==", true)
        .get()
        .then((snapshot) => {
          snapshot.forEach((donor) => {
            data.push(donor.data().email);
          });
          resolve(data);
        })
        .catch((err) => reject(err));
    } else {
      store
        .collection("requests")
        .where("city", "==", city)
        .where("bloodGroup", "in", compatibleBg[bloodGroup])
        .where("covidRecovered", "==", false)
        .get()
        .then((snapshot) => {
          snapshot.forEach((donor) => {
            data.push(donor.data().email);
          });
          resolve(data);
        })
        .catch((err) => reject(err));
    }
  });
}
